import { useCallback, useEffect, useRef, useState } from 'react';
import { message } from 'antd';
import { schoolAdminAPI } from '../../services/api';

const emptyData = () => ({ overview: null, workbench: null, grades: [], teachers: [], devices: [], failedModules: [] });
const modules = [
  ['overview', 'getOverview', '学校概况'],
  ['workbench', 'getWorkbench', '工作台'],
  ['grades', 'getGrades', '年级'],
  ['teachers', 'getTeachers', '教师'],
  ['devices', 'getDevices', '设备']
];

export default function useSchoolAdminData(schoolId) {
  const scope = schoolId ? String(schoolId) : '';
  const currentSchool = useRef(scope);
  currentSchool.current = scope;
  const requestVersion = useRef(0);
  const [state, setState] = useState(() => ({ schoolId: '', loading: false, ...emptyData() }));
  const isCurrentSchool = useCallback((target) => Boolean(target) && String(target) === currentSchool.current, []);

  const loadData = useCallback(async (targetSchoolId = scope) => {
    if (!isCurrentSchool(targetSchoolId)) return;
    const target = String(targetSchoolId);
    const version = ++requestVersion.current;
    setState({ schoolId: target, loading: true, ...emptyData() });
    const results = await Promise.allSettled(modules.map(([, method]) =>
      Promise.resolve().then(() => schoolAdminAPI[method]({ schoolId: target }))
    ));
    if (version !== requestVersion.current || !isCurrentSchool(target)) return;

    const data = emptyData();
    const failed = [];
    results.forEach((result, index) => {
      const [key, , label] = modules[index];
      if (result.status === 'fulfilled' && result.value?.data?.success) {
        data[key] = result.value.data.data ?? data[key];
      } else {
        failed.push(label);
      }
    });
    setState({ schoolId: target, loading: false, ...data, failedModules: failed });
    if (failed.length) message.warning(`学校数据加载失败：${failed.join('、')}，请刷新重试`);
  }, [scope, isCurrentSchool]);

  useEffect(() => {
    loadData();
    return () => { requestVersion.current += 1; };
  }, [loadData]);

  // Hide the previous school during the render before the new effect starts.
  const visible = state.schoolId === scope ? state : { loading: Boolean(scope), ...emptyData() };
  return { ...visible, loadData, isCurrentSchool };
}
